import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const PaymentMethodModal = ({ souscription, show, onClose, onSelectMethod }) => {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const { user } = useAuth();

  if (!show || !souscription) return null;

  // Vérifier si l'utilisateur peut voir l'option de paiement manuel
  const canAccessManualPayment = user?.role === 'admin-generale' || user?.role === 'agent-boaz';

  const paymentMethods = [
    {
      id: 'manual',
      title: '💰 Paiement Manuel',
      description: 'Enregistrer un paiement effectué par virement, chèque ou espèces',
      icon: (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      ),
      bgColor: 'from-blue-50 to-blue-100',
      borderColor: 'border-blue-200',
      hoverColor: 'hover:border-blue-400 hover:shadow-blue-100',
      available: true
    },
    {
      id: 'online',
      title: '💳 Paiement en Ligne',
      description: 'Paiement sécurisé par carte bancaire via Stripe',
      icon: (
        <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      bgColor: 'from-orange-50 to-orange-100',
      borderColor: 'border-orange-200',
      hoverColor: 'hover:border-orange-400 hover:shadow-orange-100',
      available: true // Sera disponible une fois Stripe implémenté
    }
  ];

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
  };

  const handleContinue = () => {
    if (selectedMethod) {
      onSelectMethod(selectedMethod.id, souscription);
      // Ne pas appeler onClose() ici - le parent gère la fermeture du modal
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4 z-50">
      <div className="relative mx-auto w-full max-w-2xl bg-white shadow-2xl rounded-2xl overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
          <div className="flex items-center justify-between text-white">
            <div>
              <h2 className="text-2xl font-semibold flex items-center">
                <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                Choisir la Méthode de Paiement
              </h2>
              <p className="text-blue-100 mt-1">
                Souscription: <span className="font-mono">{souscription.reference}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-red-200 transition-colors duration-200 bg-white bg-opacity-20 rounded-full p-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Info Client */}
          <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl">
            <div className="flex items-center mb-2">
              <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
              <span className="font-semibold text-gray-800">Client:</span>
            </div>
            <p className="text-gray-700">
              {souscription.prenom_client} {souscription.nom_client} - {souscription.email_client}
            </p>
          </div>

          {/* Méthodes de paiement */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Sélectionnez votre méthode de paiement :
            </h3>

            {paymentMethods
              .filter(method => method.id !== 'manual' || canAccessManualPayment)
              .map((method) => (
              <div
                key={method.id}
                className={`border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 ${
                  selectedMethod?.id === method.id
                    ? `${method.borderColor.replace('border-', 'border-')} bg-gradient-to-r ${method.bgColor} shadow-lg`
                    : `border-gray-200 bg-white hover:bg-gradient-to-r ${method.bgColor} ${method.hoverColor} shadow-md hover:shadow-lg`
                } ${!method.available ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => method.available && handleMethodSelect(method)}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {method.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {method.title}
                      </h4>
                      {selectedMethod?.id === method.id && (
                        <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                        </div>
                      )}
                    </div>
                    <p className="text-gray-600 mt-2 leading-relaxed">
                      {method.description}
                    </p>
                    {!method.available && (
                      <p className="text-orange-600 text-sm mt-2 font-medium">
                        🚧 Prochainement disponible
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-8 py-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
            >
              <span className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Annuler
              </span>
            </button>
            <button
              onClick={handleContinue}
              disabled={!selectedMethod}
              className="flex-1 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none"
            >
              <span className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                Continuer avec {selectedMethod?.title?.replace(/💰|💳/, '').trim()}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodModal;